import { config } from '@nn/nn-api';
import http from '../app-utilities/http';
import { forgeRockCodes } from './amResponseCodes';
import { getKeyByValue } from '../app-utilities/utilities';
import { sendAccountMergingStatusToAnalytics } from './analytics';
import { makeUrlParams } from './utils/helpers';

config.setFetchMethod(fetch);
config.setFetchCredentials('same-origin');

const INVALID_CREDENTIALS_RESPONSE = {
  detail: {
    failureUrl: forgeRockCodes.INVALID_CREDENTIAL,
  },
};

const props = {
  headers: {
    'X-Password': 'anonymous',
    'X-Username': 'anonymous',
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  },
};
/**
 * Call Forgerock API, with a certain configuration object.
 * @param {string} config.endpoint        Endpoint URL, expected to be configured from XC.
 * @param {string} config.code            Code value, used in query and body for Forgerock calls. Not needed for username/password calls.
 * @param {string} config.state           State value, used in query and body for Forgerock calls. Not needed for username/password calls.
 * @param {string} config.username        Username, used in body for last Forgerock call, for coupling.
 * @param {string} config.password        Password, used in body for last Forgerock call, for coupling.
 * @param {boolean} config.isCoupling     Indicates if the page is on the initial load.
 */
const isEherkenningCoupled = async (endpoint, state, code, authId) => {
  const request = { state, code, authId };
  const endpointUrl = endpoint + '?' + makeUrlParams({ state, code });

  try {
    const response = await http.post(endpointUrl.toString(), request, props);

    if (response.successUrl === forgeRockCodes.SUCCESS) {
      sendAccountMergingStatusToAnalytics(getKeyByValue(forgeRockCodes, response.successUrl).toLowerCase());
      onForgerockTreeEnd();
      return { isCoupled: true, response };
    }
    sendAccountMergingStatusToAnalytics('not_coupled');
    return { isCoupled: false, response };
  } catch (response) {
    if (!response.detail) {
      handleForgerockError(await response.json());
    } else {
      handleForgerockError(response);
    }
  }
}

const coupleEherkenning = async (endpoint, username, password) => {
  const request = { callbacks: JSON.parse(sessionStorage.getItem('callbacks')) };
  request.callbacks[0].input[0].value = username;
  request.callbacks[1].input[0].value = password;
  request.authId = sessionStorage.getItem('authId');

  try {
    const response = await http.post(endpoint.toString(), request, props);
    if (response.successUrl === forgeRockCodes.SUCCESS) {
      sendAccountMergingStatusToAnalytics(getKeyByValue(forgeRockCodes, response.successUrl).toLowerCase());
      onForgerockTreeEnd();
    } else {
      const areCredentialsRequired = response.authId && response.callbacks && response.callbacks.length >= 2 && response.callbacks[0].type === 'NameCallback' && response.callbacks[1].type === 'PasswordCallback';
      if (areCredentialsRequired) {
        sendAccountMergingStatusToAnalytics('invalid_credential');
        throw(INVALID_CREDENTIALS_RESPONSE);
      }
    }
  } catch (response) {
    if (!response.detail) {
      handleForgerockError(await response.json());
    } else {
      handleForgerockError(response);
    }
  }
}

const handleForgerockError = (response) => {
  const errorCode = response.detail ? (response.detail.failureUrl || response.detail.errorCode) : '';
  const analyticsStatus = errorCode ? getKeyByValue(forgeRockCodes, errorCode).toLowerCase() : 'error';
  sendAccountMergingStatusToAnalytics(analyticsStatus);
  // If we receive an 'invalid credentials' error, the Forgerock tree is still alive
  if (errorCode !== forgeRockCodes.INVALID_CREDENTIAL) {
    onForgerockTreeEnd();
  }
  throw new Error(errorCode || response);
};

const onForgerockTreeEnd = () => {
  sessionStorage.removeItem('authId');
};

export {
  isEherkenningCoupled,
  coupleEherkenning,
  onForgerockTreeEnd,
};
